import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { AppLoad } from '@app/core/contracts/app-load';
import { Store, StoreTheme } from '@b2b/features/misc/store/store.model';
import { STORE_CONFIG_KEY } from '@shared/tools/storage-keys';

@Injectable({ providedIn: 'root' })
export class StoreService implements AppLoad {
  private readonly baseUrl = `${environment.apiService}`;

  /**
   * Observable compartilhado com shareReplay(1): garante que apenas uma
   * requisição HTTP é feita, mesmo que múltiplos subscribers se inscrevam
   * (ex: load() e StyleThemeAdapter.getTheme() na inicialização).
   */
  private config$: Observable<Store | null> | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Chamado na inicialização do app (via APP_LOAD).
   * Busca as configurações da store no backend e persiste no localStorage.
   */
  load(): void {
    this.getConfig().subscribe();
  }

  /**
   * Retorna um Observable compartilhado com a configuração da store.
   * Realiza a requisição HTTP uma única vez e faz replay para todos os subscribers.
   * Salva o resultado no localStorage como cache para leituras síncronas posteriores.
   */
  getConfig(): Observable<Store | null> {
    if (!this.config$) {
      this.config$ = this.http.get<Store>(`${this.baseUrl}/store/config?channel=B2B_COMMERCE_PORTAL`).pipe(
        tap(store => {
          localStorage.setItem(STORE_CONFIG_KEY, JSON.stringify(store));
        }),
        catchError(err => {
          console.warn('[StoreService] Falha ao carregar store config:', err);
          return of(null);
        }),
        shareReplay(1),
      );
    }

    return this.config$;
  }

  /**
   * Leitura síncrona do cache (localStorage).
   * @returns Store ou null se não houver cache.
   */
  getStoreFromCache(): Store | null {
    const raw = localStorage.getItem(STORE_CONFIG_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Store;
  }

  /**
   * Retorna o tema visual parsado a partir do campo `themeJson` do cache.
   * @returns StoreTheme ou null se não houver cache ou themeJson inválido.
   */
  getThemeFromCache(): StoreTheme | null {
    const store = this.getStoreFromCache();
    if (!store?.themeJson) return null;
    return JSON.parse(store.themeJson) as StoreTheme;
  }
}
